import React, { useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import Http from '../../api/Http'
import { HOME_API_URL } from '../../api/path'
import files from '../../helpers/helper'
import { withTranslation } from 'react-i18next'
import useLanguage from '../../hooks/useLanguage'
import { Helmet } from "react-helmet-async"
import IconArrowRight from '../../components/svg/IconArrowRight'
import DataLoader from '../../components/common/DataLoader'
import SafeImage from '../../components/common/SafeImage'

const NewsDetail = ({ t }) => {
    const { newsId } = useParams()
    const navigate = useNavigate()
    const { language } = useLanguage()

    const newsDetailKey = useMemo(() => ["newsDetailApi", newsId], [newsId])
    
    const { data: newsDetail, isLoading } = useQuery(
        newsDetailKey,
        async () => {
            const response = await Http.get(`${HOME_API_URL.latestNewsList}/${newsId}`)
            return response?.data
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!newsId
        }
    )

    if (isLoading) {
        return <DataLoader className="!w-full !z-10 relative min-h-[calc(100vh-240px)] h-auto" />
    }

    if (!newsDetail) {
        return (
            <div className="container py-20 text-center">
                <h3 className="text-2xl font-600 text-dark mb-4">{t('common.not_found')}</h3>
                <button onClick={() => navigate('/latest-news')} className="text-black underline">
                    {t('button.back')}
                </button>
            </div>
        )
    }

    const title = language === 'ar' ? newsDetail?.ar_title : newsDetail?.title
    const description = language === 'ar' ? newsDetail?.ar_description : newsDetail?.description

    return (
        <>
            <Helmet>
                <title>{title || 'Latest News'} | Mailarti</title>
                <meta name="description" content={description || 'Read our latest news and updates'} />
            </Helmet>
            
            {/* Breadcrumb */}
            <section className='location-info-section py-4 md:py-5 bg-gray-light'>
                <div className="container">
                    <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                        <Link to={'/'}>
                            <h5 className='text-sm md:text-base font-nunito font-400 text-dark hover:text-black transition-colors'>
                                {t("front_footer.home")}
                            </h5>
                        </Link>
                        <span><IconArrowRight className="text-dark w-3 h-3 md:w-4 md:h-4 rtl:rotate-180" /></span>
                        <Link to={'/latest-news'}>
                            <h5 className='text-sm md:text-base font-nunito font-400 text-dark hover:text-black transition-colors'>
                                {t("home_module.latest_news")}
                            </h5>
                        </Link>
                        <span><IconArrowRight className="text-dark w-3 h-3 md:w-4 md:h-4 rtl:rotate-180" /></span>
                        <h5 className='text-sm md:text-base font-nunito font-400 text-black line-clamp-1 max-w-[200px] md:max-w-xs'>
                            {title}
                        </h5>
                    </div>
                </div>
            </section>

            {/* News Detail Content */}
            <section className='news-detail-section py-8 md:py-12 lg:py-16'>
                <div className="container">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10 xl:gap-12">
                        {/* Left Side - Image (Sticky on desktop) */}
                        <div className="news-image-column order-1 lg:order-none">
                            <div className="news-image-wrapper lg:sticky lg:top-20 w-full h-[280px] sm:h-[350px] md:h-[450px] lg:h-[500px] xl:h-[600px] overflow-hidden rounded-lg shadow-md">
                                <SafeImage 
                                    src={files(newsDetail?.coverImage, 'latest-news')} 
                                    alt={title || 'News image'}
                                    className='w-full h-full object-cover hover:scale-105 transition-transform duration-500'
                                />
                            </div>
                        </div>

                        {/* Right Side - Title and Description */}
                        <div className="news-content-column order-2 lg:order-none">
                            {/* Title */}
                            <h1 className='text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-cormorant font-600 rtl:font-500 text-dark mb-4 md:mb-6 lg:mb-8 leading-tight'>
                                {title}
                            </h1>

                            {/* Description */}
                            <div className="news-description">
                                <p className='text-sm sm:text-base md:text-lg text-gray-dark leading-relaxed md:leading-loose whitespace-pre-wrap'>
                                    {description}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default withTranslation()(NewsDetail)

